import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, CircularProgress } from '@mui/material';
import { IRootState } from '../../store/reducer';
import { fetchDeletionStatus, markChecked } from '../account/store/slice';
import type { Dispatch } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';

interface AccountStatusGuardProps {
  children: React.ReactNode;
}

/**
 * Checks if the authenticated user's account has been deleted.
 * If deleted, redirects to /account so the account page can show the deleted state.
 * State lives in the `account` Redux slice; consumers read via useSelector.
 * Fails open — if the check fails, the user proceeds normally.
 */
const AccountStatusGuard: React.FC<AccountStatusGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth0();
  const dispatch = useDispatch<ThunkDispatch<IRootState, unknown, ReturnType<Dispatch>>>();
  const { statusChecked, isDeleted } = useSelector((s: IRootState) => s.account);

  useEffect(() => {
    if (isLoading) return;

    const auth0Id = user?.sub;
    if (!isAuthenticated || !auth0Id) {
      dispatch(markChecked());
      return;
    }

    const promise = dispatch(fetchDeletionStatus(auth0Id));
    return () => {
      promise.abort();
    };
  }, [isLoading, isAuthenticated, user?.sub, dispatch]);

  useEffect(() => {
    if (isDeleted && window.location.pathname !== '/account') {
      window.location.replace('/account');
    }
  }, [isDeleted]);

  if (!statusChecked) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        bgcolor="#000A16"
      >
        <CircularProgress sx={{ color: 'rgba(255, 255, 255, 0.70)' }} />
      </Box>
    );
  }

  return <>{children}</>;
};

export default AccountStatusGuard;
